import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme, alpha } from '@mui/material/styles';
import {
  Download,
  Calendar,
  Brain,
  Book,
  Clock,
  Share2,
  Lightbulb,
  TrendingUp,
  BarChart3
} from 'lucide-react';

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts';

import consultService from '../services/AGTUps-product-services/consultService';

/** 
 * Paleta de colores / theme 
 * similar a los otros shortcuts
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    }
  });

// Paleta de colores para los gráficos
const CHART_COLORS = [
  'rgba(128, 128, 128, 0.6)', // Primario
  'rgba(128, 128, 128, 0.7)', // Success
  'rgba(128, 128, 128, 0.8)', // Warning
  'rgba(128, 128, 128, 0.65)', // Product
  'rgba(128, 128, 128, 0.75)', // Cash Flow
  'rgba(128, 128, 128, 0.7)', // Marketplace
  'rgba(128, 128, 128, 0.65)'  // Profile
];

// ======== Componente Shortcut: ConsultationStatsShortcut ========
function ConsultationStatsShortcut({ open, onClose }) {
  // Tomamos el modo del theme "padre"
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);
  const isDark = parentTheme.palette.mode === 'dark';

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [timeRange, setTimeRange] = useState('all');
  const [activeInsight, setActiveInsight] = useState(null);

  // Función para generar datos adicionales simulados para los gráficos
  const generateAdditionalData = () => {
    // Datos de consultas por mes
    const monthlyData = [
      { name: 'Ene', consultas: 4 },
      { name: 'Feb', consultas: 6 },
      { name: 'Mar', consultas: 8 },
      { name: 'Abr', consultas: 5 },
      { name: 'May', consultas: 12 },
      { name: 'Jun', consultas: 9 },
      { name: 'Jul', consultas: 8 },
      { name: 'Ago', consultas: 10 },
      { name: 'Sep', consultas: 14 },
      { name: 'Oct', consultas: 11 },
      { name: 'Nov', consultas: 9 },
      { name: 'Dic', consultas: 7 }
    ];

    // Datos de tipos de consultas
    const consultationTypes = [
      { name: 'Estrategia', value: 35 },
      { name: 'Finanzas', value: 25 },
      { name: 'Operaciones', value: 15 },
      { name: 'Marketing', value: 20 },
      { name: 'Otros', value: 5 }
    ];

    // Satisfacción por experto
    const expertSatisfaction = [
      { name: 'Estratega', rating: 4.8 },
      { name: 'Analista', rating: 4.6 },
      { name: 'Asesor', rating: 4.9 },
      { name: 'Innovador', rating: 4.7 },
      { name: 'Consultor', rating: 4.5 }
    ];

    // Insights generados automáticamente
    const insights = [
      {
        id: 1,
        title: 'Tendencia creciente',
        description: 'Tus consultas han aumentado un 25% en los últimos 3 meses, lo que indica una mayor utilización del servicio.',
        icon: TrendingUp
      },
      {
        id: 2,
        title: 'Experto más consultado',
        description: 'El Estratega es tu experto más consultado, sugiriendo un enfoque en decisiones estratégicas de negocio.',
        icon: Brain
      },
      {
        id: 3,
        title: 'Tiempo de respuesta',
        description: 'El tiempo promedio de respuesta es de 2.3 minutos, significativamente por debajo del promedio del sector.',
        icon: Clock
      },
      {
        id: 4,
        title: 'Temas frecuentes',
        description: 'Las consultas sobre estrategia de mercado representan el 42% de tus interacciones.',
        icon: Lightbulb
      }
    ];

    return {
      monthlyData,
      consultationTypes,
      expertSatisfaction,
      insights
    };
  };

  // Cargar estadísticas - convertido a useCallback
  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await consultService.getConsultationStats();
      if (res.success && res.data) {
        setStats(res.data);
        
        // Simulamos datos adicionales para los gráficos
        // En una implementación real, estos datos vendrían del backend
        const additionalData = generateAdditionalData();
        setStats(prev => ({
          ...prev,
          ...additionalData
        }));
      }
    } catch (error) {
      console.error('Error fetching consultation stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar estadísticas cuando se abre el shortcut
  useEffect(() => {
    if (open) {
      fetchStats();
    }
  }, [open, fetchStats]);

  // Renderizar el contenido principal del shortcut
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!stats) {
      return (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <BarChart3 size={48} opacity={0.5} />
          <Typography variant="body1" color="text.secondary">
            No hay estadísticas disponibles.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* KPI Cards */}
        <Grid container spacing={2}>
          {/* Total Consultations */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? alpha(CHART_COLORS[0], 0.2) : alpha(CHART_COLORS[0], 0.1),
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Brain size={20} color={CHART_COLORS[0]} style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" color="text.secondary">Total Consultas</Typography>
              </Box>
              <Typography variant="h3" fontWeight={600} sx={{ my: 1 }}>
                {stats.totalConsultations}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Desde el inicio
              </Typography>
            </Paper>
          </Grid>
          
          {/* Success Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? alpha(CHART_COLORS[1], 0.2) : alpha(CHART_COLORS[1], 0.1),
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp size={20} color={CHART_COLORS[1]} style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" color="text.secondary">Tasa de Éxito</Typography>
              </Box>
              <Typography variant="h3" fontWeight={600} sx={{ my: 1 }}>
                {stats.successRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Consultas completadas exitosamente
              </Typography>
            </Paper>
          </Grid>
          
          {/* Average Messages */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? alpha(CHART_COLORS[2], 0.2) : alpha(CHART_COLORS[2], 0.1),
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Clock size={20} color={CHART_COLORS[2]} style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" color="text.secondary">Promedio Mensajes</Typography>
              </Box>
              <Typography variant="h3" fontWeight={600} sx={{ my: 1 }}>
                {Number(stats.avgMessagesPerConsultation).toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mensajes por consulta
              </Typography>
            </Paper>
          </Grid>
          
          {/* Finalised */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? alpha(CHART_COLORS[3], 0.2) : alpha(CHART_COLORS[3], 0.1),
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Book size={20} color={CHART_COLORS[3]} style={{ marginRight: 8 }} />
                <Typography variant="subtitle2" color="text.secondary">Finalizadas</Typography>
              </Box>
              <Typography variant="h3" fontWeight={600} sx={{ my: 1 }}>
                {stats.finalisedConsultations}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Consultas completadas
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider />
        
        {/* Charts */}
        <Grid container spacing={3}>
          {/* Consultas por Mes */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? '#272729' : '#F2F2F7',
                height: '100%'
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Evolución de Consultas
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line 
                      type="monotone" 
                      dataKey="consultas" 
                      stroke={CHART_COLORS[0]} 
                      name="Consultas"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          
          {/* Tipos de Consulta */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? '#272729' : '#F2F2F7',
                height: '100%'
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Tipos de Consulta
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={stats.consultationTypes}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {stats.consultationTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                  </RePieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          
          {/* Satisfacción por Experto */}
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDark ? '#272729' : '#F2F2F7'
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Satisfacción por Experto
              </Typography>
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.expertSatisfaction}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 5]} />
                    <RechartsTooltip />
                    <Bar 
                      dataKey="rating" 
                      fill={CHART_COLORS[0]} 
                      name="Calificación"
                      label={{ position: 'top', formatter: (value) => value.toFixed(1) }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        
        <Divider />
        
        {/* Insights */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Insights y Recomendaciones
          </Typography>
          
          <Grid container spacing={2}>
            {stats.insights.map((insight) => (
              <Grid item xs={12} sm={6} key={insight.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 
                      activeInsight === insight.id
                        ? (isDark ? alpha(COLORS.primary, 0.2) : alpha(COLORS.primary, 0.1))
                        : (isDark ? '#272729' : '#F2F2F7'),
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDark ? alpha(COLORS.primary, 0.15) : alpha(COLORS.primary, 0.08),
                    },
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2
                  }}
                  onClick={() => setActiveInsight(insight.id === activeInsight ? null : insight.id)}
                >
                  <Box 
                    sx={{ 
                      p: 1.5, 
                      borderRadius: '50%', 
                      backgroundColor: isDark ? alpha(COLORS.primary, 0.2) : alpha(COLORS.primary, 0.1) 
                    }}
                  >
                    <insight.icon size={24} color={COLORS.primary} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {insight.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {insight.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        aria-labelledby="consultation-stats-dialog"
      >
        {/* Title + Actions */}
        <DialogTitle
          id="consultation-stats-dialog"
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Estadísticas de Consultas
          </Typography>
          <Box>
            <Tooltip title="Compartir estadísticas">
              <IconButton>
                <Share2 size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Descargar reporte">
              <IconButton>
                <Download size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        {/* Contenido Principal */}
        <DialogContent dividers sx={{ minHeight: '500px' }}>
          {renderMainContent()}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
            }}
          >
            Cerrar
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<Calendar />}
            onClick={onClose}
          >
            Programar reporte semanal
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default ConsultationStatsShortcut;